import { Controller } from "@hotwired/stimulus"

// Combobox controller for searching and selecting products via server-side search
// Renders dropdown in body to avoid overflow clipping issues
export default class extends Controller {
  static targets = ["input", "productId"]
  static values = {
    url: String
  }

  connect() {
    this.timeout = null
    this.createDropdown()
    document.addEventListener("click", this.closeOnClickOutside.bind(this))
    window.addEventListener("scroll", this.positionDropdown.bind(this), true)
    window.addEventListener("resize", this.positionDropdown.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnClickOutside.bind(this))
    window.removeEventListener("scroll", this.positionDropdown.bind(this), true)
    window.removeEventListener("resize", this.positionDropdown.bind(this))
    if (this.dropdown) {
      this.dropdown.remove()
    }
    if (this.timeout) clearTimeout(this.timeout)
  }

  createDropdown() {
    this.dropdown = document.createElement("div")
    this.dropdown.className = "hidden fixed z-[9999] bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
    this.dropdown.style.minWidth = "200px"
    document.body.appendChild(this.dropdown)

    // Use event delegation for clicks inside dropdown
    this.dropdown.addEventListener("click", (event) => {
      const button = event.target.closest("[data-product-id]")
      if (button) {
        this.selectProduct(button.dataset.productId, button.dataset.productTitle)
      }
    })
  }

  positionDropdown() {
    if (!this.dropdown || this.dropdown.classList.contains("hidden")) return

    const rect = this.inputTarget.getBoundingClientRect()
    this.dropdown.style.top = `${rect.bottom + 4}px`
    this.dropdown.style.left = `${rect.left}px`
    this.dropdown.style.width = `${rect.width}px`
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (this.timeout) clearTimeout(this.timeout)

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.timeout = setTimeout(() => {
      this.fetchResults(query)
    }, 300)
  }

  async fetchResults(query) {
    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json"
        }
      })

      if (!response.ok) throw new Error("Search failed")

      const products = await response.json()
      this.renderResults(products)
    } catch (error) {
      console.error("Product search error:", error)
      this.hideResults()
    }
  }

  renderResults(products) {
    if (products.length === 0) {
      this.dropdown.innerHTML = `
        <div class="px-4 py-3 text-sm text-gray-500 dark:text-neutral-400">
          No products found
        </div>
      `
      this.showResults()
      return
    }

    this.dropdown.innerHTML = products.map(product => `
      <button type="button"
              class="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 dark:hover:bg-neutral-700 text-left"
              data-action="click->product-combobox#select"
              data-product-id="${product.id}"
              data-product-title="${this.escapeHtml(product.title)}">
        ${product.image_url
          ? `<img src="${product.image_url}" alt="" class="w-8 h-8 object-cover rounded flex-shrink-0">`
          : `<div class="w-8 h-8 bg-gray-200 dark:bg-neutral-600 rounded flex-shrink-0"></div>`
        }
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 dark:text-white truncate">${this.escapeHtml(product.title)}</p>
          ${product.price ? `<p class="text-xs text-gray-500 dark:text-neutral-400">$${product.price}</p>` : ''}
        </div>
      </button>
    `).join('')

    this.showResults()
  }

  select(event) {
    const button = event.currentTarget
    this.selectProduct(button.dataset.productId, button.dataset.productTitle)
  }

  selectProduct(productId, productTitle) {
    this.productIdTarget.value = productId
    this.inputTarget.value = productTitle
    this.hideResults()
  }

  showResults() {
    this.dropdown.classList.remove("hidden")
    this.positionDropdown()
  }

  hideResults() {
    if (this.dropdown) {
      this.dropdown.classList.add("hidden")
    }
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target) && !this.dropdown.contains(event.target)) {
      this.hideResults()
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
